import { Button, Dropdown, Select, Space, Typography } from 'antd';
import moment, { type Moment } from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DateFormat, DateUtility } from '@utility/date';
import type { FilterConfig, FilterOption, OperatorOption } from './types';
import { DEFAULT_OPERATOR_OPTIONS } from './types';
import styled from 'styled-components';
import { MomentDatePicker } from '@elements/controlled-fields/momentDatePicker';
import { Check } from '@phosphor-icons/react';
import { Funnel } from '@phosphor-icons/react';
import { IconSizes } from '@utility/constants/style-contants';

const PANEL_WIDTH = 320;

const FilterListContainer = styled.div`
  width: ${PANEL_WIDTH}px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow:
    0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 3px 6px -4px rgba(0, 0, 0, 0.12),
    0 9px 28px 8px rgba(0, 0, 0, 0.05);
  border-radius: 5px;
`;

const FilterListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px;
  cursor: pointer;
  border-radius: 4px;
  &:hover {
    background: ${({ theme }) => theme.colors.box};
  }
`;

function normalizeOptions(
  opts: FilterConfig['options'],
): { value: string | number; label: string }[] {
  if (!opts?.length) return [];
  return opts.map(opt => ({
    value: opt.value,
    label: (opt.label ?? opt.text ?? String(opt.value)) as string,
  }));
}

function getOperatorOptions(config: FilterConfig): OperatorOption[] {
  const list = config.customOptions ?? config.queryOptions;
  return (list?.length ? list : DEFAULT_OPERATOR_OPTIONS) as OperatorOption[];
}

function getDefaultOperator(
  config: FilterConfig,
  currentFilter: Record<string, unknown>,
): string {
  const key = config.typeQuery;
  if (key && currentFilter[key] != null) return String(currentFilter[key]);
  if (config.defaultQuery) return config.defaultQuery;
  const opts = getOperatorOptions(config);
  const first = opts[0];
  return first ? String(first.value) : 'EQUAL';
}

/** Filter state can include list-filter keys (string) and parent keys (e.g. page, limit). */
export interface ListFilterProps {
  filters: FilterConfig[];
  filter: Record<string, unknown>;
  setFilter: (next: Record<string, unknown>) => void;
}

export function ListFilter({ filters, filter, setFilter }: ListFilterProps) {
  const [open, setOpen] = useState(false);
  const [selectedFilterName, setSelectedFilterName] = useState<string | null>(null);

  const visibleFilters = useMemo(() => filters?.filter(f => !f.skip), [filters]);

  const selectedConfig = useMemo(
    () => (selectedFilterName ? filters.find(f => f.name === selectedFilterName) : null),
    [filters, selectedFilterName],
  );

  const currentOperator = selectedConfig
    ? getDefaultOperator(selectedConfig, filter)
    : 'EQUAL';

  const [editorOperator, setEditorOperator] = useState(currentOperator);
  const [editorValue, setEditorValue] = useState<string | string[] | null>(null);
  const [editorDate, setEditorDate] = useState<Moment | null>(null);
  const [editorDateRange, setEditorDateRange] = useState<[Moment, Moment] | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [customSearchOptions, setCustomSearchOptions] = useState<FilterOption[]>([]);

  const goToList = useCallback(() => {
    setSelectedFilterName(null);
    setEditorValue(null);
    setEditorDate(null);
    setEditorDateRange(null);
    setSearchQuery('');
    setCustomSearchOptions([]);
  }, []);

  useEffect(() => {
    if (!open) goToList();
  }, [open, goToList]);

  const handleClearFilters = useCallback(() => {
    const next = { ...filter } as Record<string, unknown>;
    visibleFilters.forEach(config => {
      next[config.name] = undefined;
      if (config.typeQuery) next[config.typeQuery] = undefined;
    });
    next.page = 1;
    setFilter(next);
    goToList();
  }, [filter, visibleFilters, setFilter, goToList]);

  const handleClearFilter = useCallback(() => {
    if (!selectedConfig) return;
    const next = { ...filter } as Record<string, unknown>;
    next[selectedConfig.name] = undefined;
    if (selectedConfig.typeQuery) next[selectedConfig.typeQuery] = undefined;
    next.page = 1;
    setFilter(next);
    goToList();
  }, [filter, selectedConfig, setFilter, goToList]);

  const handleApply = useCallback(() => {
    if (!selectedConfig) return;
    const next = { ...filter } as Record<string, unknown>;
    const op = selectedConfig.typeQuery ? editorOperator : undefined;
    if (selectedConfig.typeQuery) {
      next[selectedConfig.typeQuery] = editorOperator;
    }

    if (selectedConfig.type === 'date') {
      const fmt = selectedConfig.dateFormat ?? DateFormat.dashDateMonthYear;
      if (
        selectedConfig.typeQuery &&
        op === 'BETWEEN' &&
        editorDateRange?.[0] &&
        editorDateRange?.[1]
      ) {
        next[selectedConfig.name] = [
          DateUtility.formatDate(editorDateRange[0], fmt),
          DateUtility.formatDate(editorDateRange[1], fmt),
        ].join(',');
      } else if (editorDate) {
        next[selectedConfig.name] = DateUtility.formatDate(editorDate, fmt);
      }
    } else {
      if (selectedConfig.multiple && Array.isArray(editorValue)) {
        next[selectedConfig.name] = editorValue.filter(Boolean).join(',');
      } else if (
        editorValue !== null &&
        editorValue !== undefined &&
        !Array.isArray(editorValue)
      ) {
        next[selectedConfig.name] = String(editorValue);
      }
    }
    setFilter(next);
    setOpen(false);
    goToList();
  }, [
    selectedConfig,
    filter,
    setFilter,
    editorOperator,
    editorValue,
    editorDate,
    editorDateRange,
    goToList,
  ]);

  const openEditor = useCallback(
    (config: FilterConfig) => {
      setSelectedFilterName(config.name);
      const existing = filter[config.name] as string | undefined;
      const existingOp = config.typeQuery
        ? (filter[config.typeQuery] as string | undefined)
        : undefined;
      setEditorOperator(
        existingOp ??
          config.defaultQuery ??
          String(getOperatorOptions(config)[0]?.value ?? 'EQUAL'),
      );
      if (config.type === 'date') {
        const fmt = config.dateFormat ?? DateFormat.dashDateMonthYear;
        if (existing?.includes(',')) {
          const [a, b] = existing.split(',');
          setEditorDate(null);
          setEditorDateRange(
            [a ? moment(a, fmt) : null, b ? moment(b, fmt) : null].filter(Boolean) as [
              Moment,
              Moment,
            ],
          );
        } else {
          setEditorDate(existing ? moment(existing, fmt) : null);
          setEditorDateRange(null);
        }
        setEditorValue(null);
      } else if (config.multiple) {
        setEditorValue(existing ? existing.split(',').filter(Boolean) : []);
        setEditorDate(null);
        setEditorDateRange(null);
      } else {
        setEditorValue(existing ?? null);
        setEditorDate(null);
        setEditorDateRange(null);
      }
      setSearchQuery('');
      setCustomSearchOptions([]);
    },
    [filter],
  );

  const selectOptions = useMemo(() => {
    if (!selectedConfig) return [];
    let opts: FilterOption[];
    if (selectedConfig.customSearch && customSearchOptions.length > 0) {
      opts = customSearchOptions;
    } else {
      opts = normalizeOptions(selectedConfig.options);
    }
    if (selectedConfig.search && searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return opts.filter(o => (o.label ?? String(o.value)).toLowerCase().includes(q));
    }
    return opts;
  }, [selectedConfig, customSearchOptions, searchQuery]);

  const handleCustomSearch = useCallback(
    async (q: string) => {
      if (!selectedConfig?.customSearch) return;
      const filteredOptions =
        selectedConfig?.options?.filter(item =>
          selectedConfig?.customSearch?.(item, q),
        ) || [];
      setCustomSearchOptions(normalizeOptions(filteredOptions));
    },
    [selectedConfig],
  );

  useEffect(() => {
    if (selectedConfig?.customSearch) {
      setCustomSearchOptions(normalizeOptions(selectedConfig.options || []));
    }
  }, [selectedConfig]);

  const popupContent = useMemo(() => {
    if (selectedFilterName && selectedConfig) {
      const isDate = selectedConfig.type === 'date';
      const opOpts = getOperatorOptions(selectedConfig);
      const operatorSelectOptions = opOpts.map(o => ({
        value: String(o.value),
        label: (o.label ?? o.text ?? String(o.value)) as string,
      }));
      const isBetween = editorOperator === 'BETWEEN';

      return (
        <FilterListContainer style={{ width: PANEL_WIDTH, padding: 12 }}>
          <Space vertical style={{ width: '100%' }} size="middle">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              <Typography.Text strong>{selectedConfig.label}</Typography.Text>
              <Space>
                {selectedConfig.typeQuery && (
                  <Select
                    size="small"
                    value={editorOperator}
                    onChange={setEditorOperator}
                    options={operatorSelectOptions}
                    style={{ minWidth: 120 }}
                  />
                )}
                <Button type="link" size="small" onClick={handleClearFilter}>
                  Clear Filter
                </Button>
              </Space>
            </div>
            {!isDate && (
              <Select
                mode={selectedConfig.multiple ? 'multiple' : undefined}
                placeholder="Select..."
                style={{ width: '100%' }}
                value={
                  selectedConfig.multiple
                    ? Array.isArray(editorValue)
                      ? editorValue
                      : editorValue
                        ? [editorValue]
                        : []
                    : ((Array.isArray(editorValue) ? editorValue[0] : editorValue) ??
                      undefined)
                }
                onChange={v =>
                  setEditorValue(selectedConfig.multiple ? (v ?? []) : (v ?? null))
                }
                options={selectOptions}
                showSearch={
                  selectedConfig.search
                    ? {
                        filterOption: selectedConfig.search
                          ? (input, opt) =>
                              (opt?.label ?? '')
                                .toLowerCase()
                                .includes(input.toLowerCase())
                          : undefined,
                        optionFilterProp: 'label',
                        onSearch: selectedConfig.customSearch
                          ? handleCustomSearch
                          : undefined,
                      }
                    : false
                }
                listHeight={256}
              />
            )}
            {isDate && (
              <>
                {isBetween ? (
                  <MomentDatePicker.RangePicker
                    value={editorDateRange}
                    onChange={(dates: [Moment | null, Moment | null] | null) => {
                      if (dates?.[0] && dates?.[1]) {
                        setEditorDateRange([dates[0], dates[1]]);
                      } else {
                        setEditorDateRange(null);
                      }
                    }}
                    disabledDate={selectedConfig.disabledDate}
                    format={selectedConfig.dateFormat ?? DateFormat.dashDateMonthYear}
                    style={{ width: '100%' }}
                  />
                ) : (
                  <MomentDatePicker
                    value={editorDate}
                    onChange={(date: Moment | null) => setEditorDate(date)}
                    disabledDate={selectedConfig.disabledDate}
                    format={selectedConfig.dateFormat ?? DateFormat.dashDateMonthYear}
                    style={{ width: '100%' }}
                  />
                )}
              </>
            )}
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setOpen(false);
                  goToList();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" onClick={handleApply}>
                Apply
              </Button>
            </Space>
          </Space>
        </FilterListContainer>
      );
    }

    return (
      <FilterListContainer style={{ width: PANEL_WIDTH, padding: 12 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <Typography.Text strong>Filter By</Typography.Text>
          <Button type="link" size="small" onClick={handleClearFilters}>
            Clear Filters
          </Button>
        </div>
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {visibleFilters?.map(config => (
            <FilterListItem
              key={config.name}
              role="button"
              tabIndex={0}
              onClick={() => openEditor(config)}
              onKeyDown={e => e.key === 'Enter' && openEditor(config)}
            >
              <span style={{ flexShrink: 0 }}>{config.icon}</span>
              <Typography.Text>{config.label}</Typography.Text>
              {filter[config.name] && <Check />}
            </FilterListItem>
          ))}
        </div>
      </FilterListContainer>
    );
  }, [
    selectedFilterName,
    selectedConfig,
    handleClearFilters,
    visibleFilters,
    editorOperator,
    handleClearFilter,
    editorValue,
    selectOptions,
    handleCustomSearch,
    editorDateRange,
    editorDate,
    handleApply,
    goToList,
    filter,
    openEditor,
  ]);

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      popupRender={() => popupContent}
      destroyOnHidden
    >
      <Button color="default">
        <Funnel size={IconSizes.small} />
        <span>Filters</span>
      </Button>
    </Dropdown>
  );
}
